import clsx from 'clsx';
import React, { ReactElement, useContext } from 'react';
import {
  DateField as AriaDateField,
  DateFieldProps as AriaDateFieldProps,
  DateInput as AriaDateInput,
  DateInputProps,
  DateSegment,
  DateValue,
  ValidationResult,
} from 'react-aria-components';
import { DateRangePickerStateContext } from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { fieldGroupStyles } from './styles';

export interface DateFieldProps<T extends DateValue>
  extends AriaDateFieldProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
}

export const DateField = <T extends DateValue>({
  ...props
}: DateFieldProps<T>): ReactElement => {
  return (
    <AriaDateField
      {...props}
      className={(renderProps) =>
        fieldGroupStyles({
          ...renderProps,
          className: 'border-none outline-none',
        })
      }
      isReadOnly={true}
    >
      <DateInput />
    </AriaDateField>
  );
};
export const focusRing = tv({
  base: 'outline outline-primary-600 p-[1px] forced-colors:outline-[Highlight] focus-visible:outline-1 -outline-offset-2',
  variants: {
    isFocusVisible: {
      false: 'outline-0',
      true: 'outline-1',
    },
    isDisabled: {
      true: 'outline-1',
    },
  },
});
const segmentStyles = tv({
  extend: focusRing,
  base: 'inline p-[2px] type-literal:px-0 rounded outline outline-0 text-gray-950 caret-transparent transition-colors',
  variants: {
    isPlaceholder: {
      true: 'text-gray-950',
      false: 'text-gray-950',
    },
    isDisabled: {
      true: 'text-gray-200',
    },
    isFocused: {
      true: 'bg-primary-100',
    },
  },
});

export const DateInput = (
  props: Omit<DateInputProps, 'children'>,
): ReactElement => {
  const stateCalendar = useContext(DateRangePickerStateContext);
  return (
    <AriaDateInput
      className={(renderProps) =>
        fieldGroupStyles({
          ...renderProps,
          className: 'pointer-events-none block w-fit border-none',
        })
      }
      {...props}
      // @ts-expect-error - inert is a valid HTML attribute but not typed in DateInputProps
      inert={true}
    >
      {(segment) => (
        <DateSegment
          segment={segment}
          className={segmentStyles({
            className: clsx(
              'text-[1rem] font-semibold leading-6 tracking-normal',
              {
                '!text-primary-600': stateCalendar?.isOpen,
              },
            ),
          })}
        />
      )}
    </AriaDateInput>
  );
};
